import { Node } from "rete"
import { Control } from "rete"
import { NodeData } from "rete/types/core/data"
import { Project } from "../state"
import { BaseComponent } from "../modelling/components/base_component"
import {
  ComponentSocketInfo,
  deriveOutputsForComponent,
  deriveSelectableSourcesForComponent,
  getComponentInfo,
  socketPrimaryText,
  socketSecondaryText,
} from "../modelling/component_info"

export interface ProjectModelReadableOptions {
  /** Show `[x, y]` positions next to each node header. Off by default. */
  includePositions?: boolean
  /**
   * Live Rete nodes (post-`fromJSON`) — required to resolve control values and
   * dropdown / multi-select options. Without these, only raw `node.data` keys
   * that look like control state are shown (no option lists).
   */
  editorNodes?: Node[]
}

export interface ComponentsReadableOptions {
  /** Group components by toolbox category. On by default. */
  groupByCategory?: boolean
}

type SelectOption = { id: number; name: string; badge?: string }

type ControlWithMeta = Control & { type?: string; props?: Record<string, unknown> }

/** Keys persisted on `node.data` that are internal graph state, not user controls. */
const INTERNAL_DATA_KEYS = new Set([
  "name",
  "_optionUpdateCounter",
  "showInDetail",
  "catMapLabels",
  "splitLabels",
  "mergedLabels",
  "mapValByLabel",
  // Table UI-only open/collapsed flag — not useful to the agent.
  "tableOpen",
])

/** Keys that together form a Table spreadsheet; rendered as one grid, not raw JSON. */
const TABLE_DATA_KEYS = new Set(["tableRows", "tableColumns", "tableCells"])

/**
 * Render the project's Rete model graph as a human-readable outline.
 * Only the model view — nodes, inputs, outputs, connections, and controls.
 */
export function projectModelToReadable(
  projectSource: Pick<Project, "model">,
  options: ProjectModelReadableOptions = {}
): string {
  const { includePositions = false, editorNodes } = options
  const model = projectSource.model
  if (!model || !model.nodes || Object.keys(model.nodes).length === 0) {
    return "(empty model)\n"
  }

  const editorNodeById = new Map<number, Node>()
  editorNodes?.forEach(n => editorNodeById.set(n.id, n))

  const nodesById = new Map<number, NodeData>()
  Object.values(model.nodes).forEach(n => nodesById.set(n.id, n))

  const label = (id: number): string => {
    const node = nodesById.get(id)
    if (!node) return `<missing> #${id}`
    const custom = (node.data as { name?: unknown })?.name
    const display = typeof custom === "string" && custom.trim() ? custom.trim() : node.name
    return `${display} #${id}`
  }

  const out: string[] = []
  const orderedNodes = Object.values(model.nodes).sort((a, b) => a.id - b.id)

  for (const node of orderedNodes) {
    let header = label(node.id)
    if (includePositions && Array.isArray(node.position)) {
      header += ` @ [${node.position[0]}, ${node.position[1]}]`
    }

    const editorNode = editorNodeById.get(node.id)
    const outline = formatNodeOutline(header, {
      description: resolveNodeDescription(node.name),
      inputs: Object.keys(node.inputs).map(key => ({
        key,
        connections: (node.inputs[key]?.connections ?? []).map(c => ({
          peerLabel: `${label(c.node)}.${ioLabelFromNode(editorNodeById.get(c.node), c.output, "output")}`,
        })),
        ioLabel: ioLabelFromNode(editorNode, key, "input"),
      })),
      outputs: Object.keys(node.outputs).map(key => ({
        key,
        connections: (node.outputs[key]?.connections ?? []).map(c => ({
          peerLabel: `${label(c.node)}.${ioLabelFromNode(editorNodeById.get(c.node), c.input, "input")}`,
        })),
        ioLabel: ioLabelFromNode(editorNode, key, "output"),
      })),
      editorNode,
      nodeData: node,
    })
    for (const line of outline) {
      out.push(line)
    }
    out.push("")
  }

  return out.join("\n").replace(/\s+$/, "") + "\n"
}

/** Display names and ids of Map layer nodes — the only public-view chat links. */
export function mapLayerOutputsToReadable(
  projectSource: Pick<Project, "model">
): string {
  const nodes = projectSource.model?.nodes
  if (!nodes) return "(none)"
  const lines = Object.values(nodes)
    .filter(node => node.name === "Map layer")
    .sort((a, b) => a.id - b.id)
    .map(node => {
      const custom = (node.data as { name?: unknown })?.name
      const display = typeof custom === "string" && custom.trim() ? custom.trim() : "Map output"
      return `- ${display} #${node.id}`
    })
  return lines.length > 0 ? lines.join("\n") : "(none)"
}

/**
 * Render every registered toolbox component as a readable outline — same shape
 * as `projectModelToReadable`, but without instance ids or connections.
 *
 * Builds a fresh template node per component via `createNode()` so outputs and
 * controls reflect the live registration (including team-defined components).
 * Falls back to curated `component_info.ts` metadata when `createNode` fails.
 */
export async function componentsToReadable(
  components: BaseComponent[],
  options: ComponentsReadableOptions = {}
): Promise<string> {
  const { groupByCategory = true } = options
  const sorted = [...components].sort((a, b) => {
    const catA = a.category ?? "Other"
    const catB = b.category ?? "Other"
    if (catA !== catB) return catA.localeCompare(catB)
    return componentDisplayName(a).localeCompare(componentDisplayName(b))
  })

  const byCategory = new Map<string, BaseComponent[]>()
  for (const component of sorted) {
    const category = component.category ?? "Other"
    const list = byCategory.get(category) ?? []
    list.push(component)
    byCategory.set(category, list)
  }

  const lines: string[] = [`Available components (${components.length}):`, ""]

  if (groupByCategory) {
    for (const category of Array.from(byCategory.keys())) {
      lines.push(`[${category}]`, "")
      const list = byCategory.get(category)
      if (!list) continue
      for (const component of list) {
        appendComponentOutline(lines, await formatComponentOutline(component))
      }
    }
  } else {
    for (const component of sorted) {
      appendComponentOutline(lines, await formatComponentOutline(component))
    }
  }

  return lines.join("\n").replace(/\s+$/, "") + "\n"
}

function appendComponentOutline(lines: string[], outline: string[]): void {
  for (const line of outline) {
    lines.push(line)
  }
  lines.push("")
}

function componentDisplayName(component: BaseComponent): string {
  return component.contextMenuName?.trim() || component.name
}

/** Canonical name passed to `add_component` / drag-and-drop. */
function componentAddName(component: BaseComponent): string {
  return component.name
}

function appendToolboxLabelLine(lines: string[], component: BaseComponent): void {
  const display = component.contextMenuName?.trim()
  if (display && display !== component.name) {
    lines.splice(1, 0, `  Toolbox label: ${display}`)
  }
}

/** Same sources as the Toolbox sidebar info panel. */
function resolveComponentDescription(component: BaseComponent): string | undefined {
  const info = getComponentInfo(component.name)
  const text = info?.description?.trim() || component.description?.trim()
  return text || undefined
}

function resolveNodeDescription(componentName: string): string | undefined {
  const text = getComponentInfo(componentName)?.description?.trim()
  return text || undefined
}

/** Turn sidebar markdown links into plain text for console output. */
function stripMarkdownLinks(text: string): string {
  return text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1 ($2)")
}

function formatDescriptionSection(description: string): string[] {
  const cleaned = stripMarkdownLinks(description)
  const lines: string[] = ["  Description:"]
  const words = cleaned.split(/\s+/)
  let current = "    "
  for (const word of words) {
    if (current.length + word.length + 1 > 96 && current.trim()) {
      lines.push(current)
      current = "    "
    }
    current += (current.trim() ? " " : "") + word
  }
  if (current.trim()) {
    lines.push(current)
  }
  return lines
}

async function formatComponentOutline(component: BaseComponent): Promise<string[]> {
  const header = componentAddName(component)
  const description = resolveComponentDescription(component)
  try {
    const node = await component.createNode()
    const outline = formatNodeOutline(header, {
      description,
      inputs: Array.from(node.inputs.values()).map(input => ({
        key: String(input.key),
        connections: [],
        ioLabel: ioLabelFromNode(node, String(input.key), "input"),
      })),
      outputs: Array.from(node.outputs.values()).map(output => ({
        key: String(output.key),
        connections: [],
        ioLabel: ioLabelFromNode(node, String(output.key), "output"),
      })),
      editorNode: node,
    })
    appendToolboxLabelLine(outline, component)
    return outline
  } catch {
    return formatComponentOutlineFromInfo(component, header)
  }
}

function formatComponentOutlineFromInfo(component: BaseComponent, header: string): string[] {
  const info = getComponentInfo(component.name)
  const derivedOutputs = deriveOutputsForComponent(component)
  const outputs = derivedOutputs ?? info?.outputs
  const variantList = deriveSelectableSourcesForComponent(component)

  const lines = formatNodeOutline(header || componentAddName(component), {
    description: resolveComponentDescription(component),
    inputs: (info?.inputs ?? []).map(socket => ({
      key: socket.name,
      connections: [],
      ioLabel: formatStaticSocketLabel(socket),
    })),
    outputs: (outputs ?? []).map(socket => ({
      key: socket.name,
      connections: [],
      ioLabel: formatStaticSocketLabel(socket),
    })),
  })
  appendToolboxLabelLine(lines, component)

  if (variantList && variantList.items.length > 0) {
    lines.push(`  Controls:`)
    lines.push(`    ${variantList.label}: (default)`)
    lines.push(`      options: ${variantList.items.map((item, idx) => {
      const badge = variantList.itemBadges?.[idx]
      return badge ? `[${badge}] ${item}` : item
    }).join(" | ")}`)
  }

  return lines
}

function formatStaticSocketLabel(socket: ComponentSocketInfo): string {
  const primary = socketPrimaryText(socket)
  const secondary = socketSecondaryText(socket)
  if (secondary && secondary !== primary) {
    return `${primary} (${secondary})`
  }
  return primary
}

interface OutlineIo {
  key: string
  ioLabel: string
  connections: Array<{ peerLabel: string }>
}

interface NodeOutlineSpec {
  inputs: OutlineIo[]
  outputs: OutlineIo[]
  editorNode?: Node
  nodeData?: NodeData
  description?: string
}

function formatNodeOutline(header: string, spec: NodeOutlineSpec): string[] {
  const out: string[] = [`${header}:`]

  if (spec.description) {
    const descLines = formatDescriptionSection(spec.description)
    for (const line of descLines) {
      out.push(line)
    }
  }

  out.push(`  Inputs:`)
  if (spec.inputs.length === 0) {
    out.push(`    (none)`)
  } else {
    for (const input of spec.inputs) {
      if (input.connections.length === 0) {
        out.push(`    ${input.ioLabel}`)
      } else {
        for (const c of input.connections) {
          out.push(`    ${input.ioLabel} <- ${c.peerLabel}`)
        }
      }
    }
  }

  out.push(`  Outputs:`)
  if (spec.outputs.length === 0) {
    out.push(`    (none)`)
  } else {
    for (const output of spec.outputs) {
      if (output.connections.length === 0) {
        out.push(`    ${output.ioLabel}`)
      } else {
        for (const c of output.connections) {
          out.push(`    ${output.ioLabel} -> ${c.peerLabel}`)
        }
      }
    }
  }

  const controlLines = resolveControlLines(spec)
  if (controlLines.length > 0) {
    out.push(`  Controls:`)
    for (const line of controlLines) {
      out.push(line)
    }
  }

  return out
}

function resolveControlLines(spec: NodeOutlineSpec): string[] {
  if (spec.editorNode) {
    return formatNodeControlsFromEditor(spec.editorNode)
  }
  if (spec.nodeData) {
    return formatNodeControlsFromData(spec.nodeData)
  }
  return []
}

function ioLabelFromNode(
  editorNode: Node | undefined,
  ioKey: string,
  kind: "input" | "output"
): string {
  if (!editorNode) return ioKey
  const io = kind === "input" ? editorNode.inputs.get(ioKey) : editorNode.outputs.get(ioKey)
  const name = io?.name
  if (!name || name === ioKey) return ioKey
  return `${name} (${ioKey})`
}

function formatNodeControlsFromEditor(editorNode: Node): string[] {
  const lines: string[] = []
  editorNode.controls.forEach(control => {
    appendLines(lines, describeControl(control as ControlWithMeta))
  })
  editorNode.inputs.forEach(input => {
    if (input.control) {
      appendLines(lines, describeControl(input.control as ControlWithMeta, String(input.key)))
    }
  })
  return lines
}

function appendLines(target: string[], source: string[]): void {
  for (const line of source) {
    target.push(line)
  }
}

function formatNodeControlsFromData(nodeData: NodeData): string[] {
  const data = (nodeData.data ?? {}) as Record<string, unknown>
  const entries = Object.entries(data).filter(
    ([k]) => !INTERNAL_DATA_KEYS.has(k) && !TABLE_DATA_KEYS.has(k) && !k.startsWith("catVal_")
  )
  const lines = entries.map(([k, v]) => `    ${k}: ${formatDataValue(v)}`)

  if (
    Array.isArray(data.tableRows) ||
    Array.isArray(data.tableColumns) ||
    Array.isArray(data.tableCells)
  ) {
    appendLines(
      lines,
      formatTableGrid(
        readStringArray(data.tableRows),
        readStringArray(data.tableColumns),
        readTableCells(data.tableCells)
      )
    )
  }

  return lines
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(v => String(v ?? "")) : []
}

function readTableCells(value: unknown): Array<Array<number | null>> {
  if (!Array.isArray(value)) return []
  return value.map(row =>
    Array.isArray(row)
      ? row.map(cell => {
          const n = typeof cell === "number" ? cell : Number(cell)
          return typeof n === "number" && Number.isFinite(n) ? n : null
        })
      : []
  )
}

/**
 * Render a Table spreadsheet as an indented text grid the agent can read.
 * Empty tables are called out explicitly so the model doesn't invent values.
 */
function formatTableGrid(
  rows: string[],
  columns: string[],
  cells: Array<Array<number | null>>
): string[] {
  if (rows.length === 0 && columns.length === 0) {
    return ["    table: (empty — no rows or columns)"]
  }

  const colCount = Math.max(
    columns.length,
    ...cells.map(row => (Array.isArray(row) ? row.length : 0)),
    0
  )
  const rowCount = rows.length
  const headerLabels =
    columns.length > 0
      ? columns.map(c => (c.trim() ? c : "(unnamed)"))
      : Array.from({ length: colCount }, (_, i) => `col ${i + 1}`)
  const header = ["", ...headerLabels]

  const body: string[][] = []
  for (let r = 0; r < rowCount; r++) {
    const rowLabel = rows[r]?.trim() ? rows[r] : `(row ${r + 1})`
    const values: string[] = []
    for (let c = 0; c < colCount; c++) {
      const raw = cells[r] ? cells[r][c] : null
      values.push(raw === null || raw === undefined ? "" : String(raw))
    }
    body.push([rowLabel, ...values])
  }

  const widths = header.map((cell, idx) =>
    Math.max(cell.length, ...body.map(row => (row[idx] ?? "").length), 1)
  )

  const pad = (cell: string, idx: number) => cell.padEnd(widths[idx] ?? cell.length, " ")
  const lines: string[] = [
    `    table: ${rowCount} row${rowCount === 1 ? "" : "s"} × ${colCount} column${
      colCount === 1 ? "" : "s"
    }`,
    `      | ${header.map(pad).join(" | ")} |`,
    `      | ${widths.map(w => "-".repeat(w)).join(" | ")} |`,
  ]
  for (const row of body) {
    lines.push(`      | ${row.map(pad).join(" | ")} |`)
  }
  return lines
}

function describeControl(control: ControlWithMeta, namePrefix?: string): string[] {
  const type = control.type
  const props = control.props
  const fieldName = (label: string | undefined, fallback: string) => {
    const base = label?.trim() || fallback
    return namePrefix ? `${namePrefix}.${base}` : base
  }

  if (!type || !props) {
    return [`    ${fieldName(undefined, control.key)}: ?`]
  }

  try {
    switch (type) {
      case "SelectControl":
        return formatSelectControl(props, fieldName, control.key)
      case "CheckboxSelectControl":
        return formatCheckboxSelectControl(props, fieldName, control.key)
      case "BooleanControl":
        return formatBooleanControl(props, fieldName, control.key)
      case "TextControl":
        return formatTextControl(props, fieldName, control.key)
      case "TextNumericControl":
        return formatTextNumericControl(props, fieldName)
      case "NumericConstantRowControl":
        return formatNumericConstantRowControl(props, fieldName)
      case "ExposeToggleControl":
        return formatExposeToggleControl(props, fieldName)
      case "DateControl":
        return formatDateControl(props, fieldName, control.key)
      case "TableEditorControl":
        return formatTableEditorControl(props)
      case "OSMCustomQueryControl":
        return formatOSMCustomQueryControl(props, fieldName, control.key)
      default:
        return formatGenericControl(control, props, fieldName)
    }
  } catch {
    return [`    ${fieldName(undefined, control.key)}: <error reading control>`]
  }
}

function formatOSMCustomQueryControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getQueries = props.getQueries as (() => unknown) | undefined
  const raw = typeof getQueries === "function" ? getQueries() : undefined
  const queries = Array.isArray(raw) ? raw : []
  const summary = queries.length
    ? queries
        .map(q => {
          if (!q || typeof q !== "object") return String(q)
          const rec = q as { key?: unknown; value?: unknown; type?: unknown }
          const name = String(rec.key ?? "").trim()
          const val = String(rec.value ?? "").trim()
          const type = String(rec.type ?? "way").trim()
          const typeSuffix = type && type !== "way" ? ` (${type})` : ""
          return `${name}${val ? `=${val}` : ""}${typeSuffix}`
        })
        .join(", ")
    : "(none)"
  return [
    `    ${fieldName("Build Query", key)}: ${summary}`,
    `      set as [{key, value, type}] e.g. [{"key":"landuse","value":"residential","type":"way"}]; type is node|way|relation|all`,
  ]
}

function formatTableEditorControl(props: Record<string, unknown>): string[] {
  const getRows = props.getRows as (() => string[]) | undefined
  const getColumns = props.getColumns as (() => string[]) | undefined
  const getCells = props.getCells as (() => Array<Array<number | null>>) | undefined
  const rows = getRows?.() ?? []
  const columns = getColumns?.() ?? []
  const cells = getCells?.() ?? []
  return formatTableGrid(rows, columns, cells)
}

function formatSelectControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getOptions = props.getOptions as (() => SelectOption[]) | undefined
  const getId = props.getId as (() => string | undefined) | undefined
  const label = props.label as string | undefined
  const options = getOptions?.() ?? []
  const selectedId = getId?.()
  const selected = options.find(o => String(o.id) === String(selectedId))
  const optionText = (option: SelectOption) => option.badge ? `${option.badge}: ${option.name}` : option.name
  const lines = [`    ${fieldName(label, key)}: ${selected ? optionText(selected) : (selectedId ?? "(unset)")}`]
  if (options.length > 0) {
    lines.push(`      options: ${options.map(optionText).join(" | ")}`)
  }
  return lines
}

function formatCheckboxSelectControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getOptions = props.getOptions as (() => SelectOption[]) | undefined
  const getId = props.getId as (() => string[] | undefined) | undefined
  const label = props.label as string | undefined
  const options = getOptions?.() ?? []
  const selectedIds = new Set((getId?.() ?? []).map(String))
  const selectedNames = options
    .filter(o => selectedIds.has(String(o.id)))
    .map(o => o.name)
  const lines = [
    `    ${fieldName(label, key)}: ${
      selectedNames.length > 0 ? selectedNames.join(", ") : "(none selected)"
    }`,
  ]
  if (options.length > 0) {
    lines.push(`      options: ${options.map(o => o.name).join(" | ")}`)
  }
  return lines
}

function formatBooleanControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getValue = props.getValue as (() => boolean) | undefined
  const label = props.label as string | undefined
  const val = getValue?.() ?? false
  return [`    ${fieldName(label, key)}: ${val ? "yes" : "no"}`]
}

function formatTextControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getValue = props.getValue as (() => string) | undefined
  const title = props.title as string | undefined
  const val = getValue?.() ?? ""
  return [`    ${fieldName(title, key)}: ${JSON.stringify(val)}`]
}

function formatTextNumericControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string
): string[] {
  const getTextValue = props.getTextValue as (() => string) | undefined
  const getNumericValue = props.getNumericValue as (() => string) | undefined
  const textTitle = props.textTitle as string | undefined
  const numericTitle = props.numericTitle as string | undefined
  const lines: string[] = []
  if (getTextValue) {
    lines.push(`    ${fieldName(textTitle, "text")}: ${JSON.stringify(getTextValue() ?? "")}`)
  }
  if (getNumericValue) {
    lines.push(`    ${fieldName(numericTitle, "value")}: ${JSON.stringify(getNumericValue() ?? "")}`)
  }
  return lines.length > 0 ? lines : [`    ${fieldName(undefined, "control")}: ?`]
}

function formatNumericConstantRowControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string
): string[] {
  const getValue = props.getValue as (() => string) | undefined
  const getExpose = props.getExpose as (() => boolean) | undefined
  return [
    `    ${fieldName(undefined, "Value")}: ${JSON.stringify(getValue?.() ?? "")}`,
    `    ${fieldName(undefined, "expose")}: ${getExpose?.() ? "yes" : "no"}`,
  ]
}

function formatExposeToggleControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string
): string[] {
  const getExpose = props.getExpose as (() => boolean) | undefined
  return [`    ${fieldName(undefined, "expose")}: ${getExpose?.() ? "yes" : "no"}`]
}

function formatDateControl(
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string,
  key: string
): string[] {
  const getValue = props.getValue as (() => string) | undefined
  const label = props.label as string | undefined
  return [`    ${fieldName(label, key)}: ${JSON.stringify(getValue?.() ?? "")}`]
}

function formatGenericControl(
  control: ControlWithMeta,
  props: Record<string, unknown>,
  fieldName: (label: string | undefined, fallback: string) => string
): string[] {
  const getValue = props.getValue as (() => unknown) | undefined
  if (typeof getValue === "function") {
    return [`    ${fieldName(undefined, control.key)}: ${formatDataValue(getValue())}`]
  }
  return [`    ${fieldName(undefined, control.key)} (${control.type})`]
}

function formatDataValue(value: unknown): string {
  if (value === null) return "null"
  if (value === undefined) return "undefined"
  if (typeof value === "string") return JSON.stringify(value)
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  const json = JSON.stringify(value)
  return json.length > 120 ? `${json.slice(0, 117)}...` : json
}
